<template>
    <div class="message-bubbles">
        <template v-for="(message, index) in messages">
            <!-- Show animated messages that have been revealed -->
            <div
                v-if="index < visible_message_count"
                :key="'msg-' + index"
                class="message"
                :class="{
                    'customer-message': message.type === 'customer',
                    'message-animated': index >= initial_message_count
                }"
            >
                <div class="message-bubble">
                    <template v-if="message.link && message.text.includes('{link}')">
                        <template v-for="(part, partIndex) in splitTextByLink(message.text)">
                            <span
                                v-if="part.type === 'text'"
                                :key="'text-' + partIndex"
                            >{{ part.content }}</span>
                            <a
                                v-else
                                :key="'link-' + partIndex"
                                :href="message.link.url"
                                class="message-link inline-link"
                                @click="handleLinkClick($event, message.link)"
                            >{{ message.link.text }}</a>
                        </template>
                    </template>
                    <template v-else>
                        {{ message.text }}
                        <a
                            v-if="message.link"
                            :href="message.link.url"
                            class="message-link"
                            @click="handleLinkClick($event, message.link)"
                        >
                            {{ message.link.text }}
                        </a>
                    </template>
                </div>
                <div
                    v-if="message.time"
                    class="message-time"
                >
                    {{ message.time }}
                </div>
            </div>
        </template>

        <!-- Typing indicator for next message -->
        <div
            v-if="show_typing && typing_message_type"
            class="message"
            :class="{ 'customer-message': typing_message_type === 'customer' }"
        >
            <typing-indicator variant="sms" />
        </div>
    </div>
</template>

<script>
export default {
    name: 'MockTextConversation',

    components: {
        TypingIndicator: () => import('./typing-indicator.vue')
    },

    props: {
        messages: {
            type: Array,
            required: true,
            validator: (messages) => {
                return messages.every(message =>
                    ['company', 'customer', 'typing'].includes(message.type) &&
                    (message.type === 'typing' || message.text)
                );
            }
        },

        animate_new_messages: {
            type: Boolean,
            default: true
        },

        typing_duration: {
            type: Number,
            default: 1200
        },

        /**
         * number of messages that were already shown before this mount
         * these won't animate in - they'll appear immediately
        */
        previously_shown_count: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            visible_message_count: 0,
            initial_message_count: 0,
            show_typing: false,
            typing_message_type: null,
            animation_in_progress: false
        };
    },

    watch: {
        messages: {
            handler(new_messages, old_messages) {
                const old_length = old_messages?.length || 0;
                const new_length = new_messages?.length || 0;

                if (new_length > old_length && this.animate_new_messages) {
                    // new messages added - animate them in
                    this.animateNewMessages(old_length, new_length);
                } else if (new_length < old_length) {
                    // messages removed - show all immediately
                    this.visible_message_count = new_length;
                    this.initial_message_count = new_length;
                }
            },
            deep: true
        }
    },

    methods: {
        splitTextByLink(text) {
            const parts = text.split('{link}');
            const result = [];
            parts.forEach((part, index) => {
                if (part) {
                    result.push({ type: 'text', content: part });
                }
                if (index < parts.length - 1) {
                    result.push({ type: 'link' });
                }
            });
            return result;
        },

        handleLinkClick(event, link) {
            if (link.preventDefault) {
                event.preventDefault();
            }
            if (link.callback) {
                link.callback(event);
            }
        },

        async animateNewMessages(start_index, end_index) {
            if (this.animation_in_progress) return;
            this.animation_in_progress = true;

            for (let i = start_index; i < end_index; i++) {
                const message = this.messages[i];
                if (!message) continue;

                this.typing_message_type = message.type;
                this.show_typing = true;

                await this.delay(this.typing_duration);

                this.show_typing = false;
                this.typing_message_type = null;
                this.visible_message_count = i + 1;

                // small delay between messages if there are more
                if (i < end_index - 1) {
                    await this.delay(300);
                }
            }

            this.animation_in_progress = false;
        },

        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
    },

    mounted() {
        const already_shown = Math.min(this.previously_shown_count, this.messages.length);
        this.visible_message_count = already_shown;
        this.initial_message_count = already_shown;

        // if new messages beyond what was previously shown, animate them
        if (this.messages.length > already_shown && this.animate_new_messages) {
            this.animateNewMessages(already_shown, this.messages.length);
        } else {
            this.visible_message_count = this.messages.length;
            this.initial_message_count = this.messages.length;
        }
    }
}
</script>

<style lang="scss" scoped>
.message-bubbles {
    --incoming-color: #E5E5EA;
    --outgoing-color: #0B93F6;

    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .message {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        &.customer-message {
            align-items: flex-start;
        }

        .message-bubble {
            position: relative;
            max-width: min(450px, 85%);
            background-color: var(--outgoing-color);
            color: white;
            padding: 0.75rem 1rem;
            border-radius: 1.5rem 1.5rem 0.25rem 1.5rem;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 0.875rem;
            line-height: 1.4;
            text-align: left;
            word-wrap: break-word;
            white-space: pre-line;

            &::after {
                content: '';
                position: absolute;
                right: -0.5rem;
                bottom: 0;
                width: 1rem;
                height: 1rem;
                background: var(--outgoing-color);
                clip-path: polygon(0 0, 100% 100%, 0 100%);
                border-bottom-right-radius: 0.25rem;
                z-index: 1;
            }

            .message-link {
                color: white;
                text-decoration: underline;
                word-break: break-all;

                &:not(.inline-link) {
                    display: block;
                    margin-top: 0.5rem;
                }

                &.inline-link {
                    display: inline;
                }

                &:hover {
                    text-decoration: underline;
                }
            }
        }

        &.customer-message .message-bubble {
            background-color: var(--incoming-color);
            color: black;
            border-radius: 1.5rem 1.5rem 1.5rem 0.25rem;

            &::after {
                left: -0.5rem;
                right: auto;
                background: var(--incoming-color);
                clip-path: polygon(100% 0, 100% 100%, 0 100%);
                border-bottom-right-radius: 0;
                border-bottom-left-radius: 0.25rem;
            }

            .message-link {
                color: #007AFF;
                text-decoration: underline;

                &:hover {
                    text-decoration: underline;
                }
            }
        }

        .message-time {
            font-size: 0.75rem;
            color: #666;
            margin-top: 0.25rem;
            margin-left: 0.5rem;
            margin-right: 0.5rem;
        }

        &.customer-message .message-time {
            margin-left: 0.5rem;
            margin-right: 0;
        }

        &.message-animated {
            animation: messageSlideIn 0.3s ease-out;
        }
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes messageSlideIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>